import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { scrollToId } from "@/App";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block will-change-transform"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "38%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="hero" ref={ref} className="relative min-h-[92vh] overflow-hidden pt-28" data-testid="hero-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-12 lg:gap-8 lg:px-12">
        <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 pt-10 lg:col-span-7 lg:pt-20">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.35 }}
            className="mb-10 font-mono text-[11px] uppercase tracking-[0.3em] text-gold-dark"
            data-testid="hero-eyebrow"
          >
            A network within the Church
          </motion.p>

          <h1 className="font-serif font-light leading-[0.98] tracking-tighter text-charcoal text-4xl sm:text-5xl lg:text-6xl" data-testid="hero-heading">
            <MaskedLine delay={0.15}>Righteous business,</MaskedLine>
            <MaskedLine delay={0.3}>
              <span className="font-medium">gathered</span>
            </MaskedLine>
            <MaskedLine delay={0.45}>
              <span className="italic text-gold-dark">as one guild.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 0.9 }}
            className="mt-10 max-w-xl text-base leading-relaxed text-charcoal/70 sm:text-lg"
            data-testid="hero-subtitle"
          >
            The Joseph Guild unites purpose-driven business owners within the Church — to connect,
            support one another, exchange opportunities, and collectively grow.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 1.1 }}
            className="mt-12 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToId("register")}
              className="group inline-flex items-center gap-3 rounded-full bg-charcoal px-8 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-ivory transition-colors duration-300 hover:bg-gold"
              data-testid="hero-register-cta"
            >
              Register your business
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => scrollToId("directory")}
              className="inline-flex items-center gap-3 rounded-full border border-charcoal/25 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-charcoal transition-colors duration-300 hover:border-gold hover:text-gold-dark"
              data-testid="hero-directory-cta"
            >
              Explore the directory
              <ArrowDown size={14} />
            </button>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.6, ease: EASE, delay: 0.5 }}
            className="absolute -left-8 top-16 hidden h-72 w-56 rotate-[-4deg] overflow-hidden lg:block"
            aria-hidden="true"
          >
            <img
              src="https://images.unsplash.com/photo-1545873509-33e944ca7655?auto=format&fit=crop&w=800&q=80"
              alt=""
              className="h-full w-full object-cover opacity-70"
            />
          </motion.div>

          <motion.figure
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.4, ease: EASE, delay: 0.35 }}
            className="relative ml-auto aspect-[3/4] w-full max-w-md overflow-hidden"
            data-testid="hero-image-frame"
          >
            <motion.img
              style={{ y: imgY }}
              src="https://images.pexels.com/photos/4640877/pexels-photo-4640877.jpeg"
              alt="Sunlight filtering through a cathedral's arched ceiling"
              className="h-[120%] w-full scale-110 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/25 via-transparent to-transparent" />
          </motion.figure>

          <motion.figcaption
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="mt-5 text-right font-mono text-[10px] uppercase tracking-[0.28em] text-charcoal/45"
          >
            Fig. 01 — Light through the nave
          </motion.figcaption>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.8 }}
        className="pointer-events-none absolute bottom-8 left-1/2 hidden -translate-x-1/2 lg:block"
        aria-hidden="true"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          className="h-12 w-px bg-gradient-to-b from-gold to-transparent"
        />
      </motion.div>
    </section>
  );
};
